import { Button, Spinner } from "@design";
import type { UseQueryResult } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, type GetLicenseDetailResponse } from "@/api";
import { DefinitionList, PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { formatDate, formatDateTime, formatList } from "@/i18n/format";
import { LicenseForm } from "./LicenseForm";

type License = NonNullable<GetLicenseDetailResponse["data"]>;

/** The read-only configuration rows. */
function useConfigItems(license: License) {
  const { t } = useTranslation("adminLicense");
  const none = t("config.none");
  const domains = license.exemptDomains ?? [];
  return [
    { label: t("config.name"), value: license.licenseName || none },
    { label: t("config.seatLimit"), value: license.seatLimit ?? t("config.unlimited") },
    { label: t("config.expiresAt"), value: license.expiresAt ? formatDate(license.expiresAt) : t("status.never") },
    { label: t("config.exemptDomains"), value: domains.length ? formatList(domains) : none },
    { label: t("config.notes"), value: license.notes || none },
    { label: t("config.updatedAt"), value: license.updatedAt ? formatDateTime(license.updatedAt) : none },
  ];
}

function ConfigDefinitions({ license }: Readonly<{ license: License }>) {
  return <DefinitionList items={useConfigItems(license)} />;
}

function LoadError({ error }: Readonly<{ error: unknown }>) {
  const { t } = useTranslation("adminLicense");
  const missing = error instanceof ApiError && error.status === 404;
  return (
    <Text variant="body-sm" tone={missing ? "secondary" : "error"} role={missing ? undefined : "alert"}>
      {missing ? t("config.notConfigured") : t("config.loadFailed")}
    </Text>
  );
}

/** Install admins: the license configuration, read-only until Edit. */
export function LicenseConfigSection({
  detail,
  canEdit,
}: Readonly<{ detail: UseQueryResult<GetLicenseDetailResponse>; canEdit: boolean }>) {
  const { t } = useTranslation("adminLicense");
  const [editing, setEditing] = useState(false);
  const license = detail.data?.data;
  const showForm = Boolean(license && editing && canEdit);

  let body: ReactNode = null;
  if (detail.isPending) body = <Spinner />;
  else if (detail.isError) body = <LoadError error={detail.error} />;
  else if (license && showForm) body = <LicenseForm license={license} onDone={() => setEditing(false)} />;
  else if (license) body = <ConfigDefinitions license={license} />;

  return (
    <PageSection
      title={t("config.title")}
      description={t("config.description")}
      actions={
        canEdit && license && !editing ? (
          <Button variant="secondary" leadingIcon={<Pencil size={16} />} onClick={() => setEditing(true)}>
            {t("config.edit")}
          </Button>
        ) : undefined
      }
    >
      {body}
    </PageSection>
  );
}
